import React, { useMemo, useState } from "react";

import * as d3 from "d3";
import { useIntl } from "react-intl";

import { TimeFrameType } from "src/organization/pages/project/environment/metrics/resources/types";
import { getXTickCount } from "src/organization/pages/project/environment/metrics/utils/helper";

import { HttpLayout } from "../components";
import NoData from "../components/nodata";
import { httpStatusOptions } from "../config";
import { useHttpMetricsContext } from "../context";
import {
  formatNumber,
  getCursorDataFunc,
  getDefaultDomain,
  getLinearAxis,
  getNextDivisible,
  useCursorDataSync
} from "../helper";
import useUpdateDimension from "../hook/useUpdateDimension";
import { findMetricsValue } from "../impact/util";
import * as S from "../style";
import { HttpMetricsType } from "../types";

import Area from "./Area";
import HttpStatusFilter from "./StatusOptions";
import { StatusFilterWrapper } from "./style";
import RequestTooltip from "./Tooltip";
import { HttpRequestSeries, RequestMetrics, RequestStatusUnion } from "./type";
import { groupMetricsByStatus } from "./utils";

const chartId = "http-request";
const NUM_OF_Y_TICKS = 5;

const getDomains = (data: HttpRequestSeries[], timeframe?: TimeFrameType) => {
  const flatSeries = data.flat();
  const defaultDomain = getDefaultDomain(timeframe);
  const actualYMax = d3.max(flatSeries, d => Math.max(d[0], d[1]));

  //For this graph we want to always keep the yMax to be a multiple of number of y ticks
  //This is to ensure that the y axis ticks does not have fractions
  const adjustedYMax = getNextDivisible(
    actualYMax || NUM_OF_Y_TICKS,
    NUM_OF_Y_TICKS
  );

  const domains = {
    xDomain: defaultDomain.xDomain,
    yDomain: [0, adjustedYMax]
  };
  return flatSeries.length ? domains : defaultDomain;
};
type HttpRequestProps = {
  data: HttpMetricsType[];
  rates: HttpMetricsType[];
};
const HttpRequest = (props: HttpRequestProps) => {
  const intl = useIntl();

  const { data, rates } = props;

  const [httpStatuses, setHttpStatus] = useState<string[]>(
    httpStatusOptions as unknown as string[]
  );

  const context = useHttpMetricsContext();
  const { timeframe, setCursorData } = context || {};

  const dimension = useUpdateDimension(chartId);

  const stackedSeries = useMemo(() => {
    const groups = groupMetricsByStatus(data);
    const statusIntersection = Array.from(
      d3.intersection(groups.statuses, httpStatuses)
    ) as RequestStatusUnion[];

    const stack = d3.stack<RequestMetrics, RequestStatusUnion>();
    const stackedSeries = stack.keys(statusIntersection)(groups.data);

    return stackedSeries;
  }, [data, httpStatuses]);

  const { xDomain, yDomain } = useMemo(
    () => getDomains(stackedSeries, timeframe),
    [stackedSeries, timeframe]
  );

  const { xScale, yScale, positions } = getLinearAxis({
    dimension: dimension,
    xDomain,
    yDomain
  });

  const disable = !stackedSeries.length;
  const tickX = getXTickCount(dimension.width, timeframe);

  const rate = parseFloat(
    findMetricsValue(rates[rates.length - 1], "request.rate")?.sum.toFixed(2) ||
      "0"
  );

  const cursorFuncProps = useMemo(
    () => ({
      xScale,
      yScale,
      data: stackedSeries[0],
      setCursorData,
      bisectFn: d => d.data.date,
      disable,
      chartId
    }),
    [disable, setCursorData, stackedSeries, xScale, yScale]
  );

  const cursorSync = useCursorDataSync({ cursorFuncProps });
  const adaptedCursor = cursorSync?.adaptedCursor;
  return (
    <>
      <S.ChartTitleContainer>
        <S.ChartTitleList>
          <S.ChartTitle tag="h3">
            {intl.formatMessage({ id: "http_request" })}
          </S.ChartTitle>
          {/* //TODO disabled feature enable when filter is included */}
          {/* <ChartInfo
            link={
              <Link href="j">{intl.formatMessage({ id: "learn_more" })}</Link>
            }
            title={intl.formatMessage({ id: "total_bandwidth" })}
            description={intl.formatMessage({ id: "http.bandwidth.info" })}
          /> */}
          <StatusFilterWrapper>
            <HttpStatusFilter
              setHttpStatus={setHttpStatus}
              httpStatuses={httpStatuses}
            />
          </StatusFilterWrapper>
        </S.ChartTitleList>
        <S.ChartTitle className="sum" tag="h3">
          {formatNumber({ value: rate })}/sec
        </S.ChartTitle>
      </S.ChartTitleContainer>
      <HttpLayout
        chartId={chartId}
        yScale={yScale}
        xScale={xScale}
        positions={positions}
        tick={{
          y: {
            format: value => formatNumber({ value })
          },
          x: tickX
        }}
        onMouseMove={getCursorDataFunc(cursorFuncProps)}
        onMouseLeave={() => setCursorData?.()}
        cursorData={adaptedCursor}
      >
        {disable ? (
          <NoData />
        ) : (
          stackedSeries.map((data, i) => (
            <Area
              chartId={chartId}
              key={`request_stack_${i}_${data.key}`}
              xScale={xScale}
              yScale={yScale}
              data={data}
            />
          ))
        )}
      </HttpLayout>
      {adaptedCursor?.isInCursorGroup && (
        <RequestTooltip cursorSync={cursorSync} series={stackedSeries} />
      )}
    </>
  );
};

export default HttpRequest;
